import type { Experience } from '@buakei/types';
import { LoadingState, PermissionDeniedState } from '@buakei/ui';
import type { ReactNode } from 'react';
import { Link, Navigate, useLocation } from 'react-router';
import { EXPERIENCE_META } from '@/shell/nav-config';
import { useAuth } from './session';

/** Redirects anonymous visitors to sign-in. Experience only — NOT an authorisation control. */
export function RequireSession({ children }: { children: ReactNode }) {
  const { session, status } = useAuth();
  const location = useLocation();
  if (!session && status === 'checking') {
    return <LoadingState label="Checking your sign-in" className="mx-auto w-full max-w-xl p-6" />;
  }
  if (!session) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/sign-in?next=${next}`} replace />;
  }
  return <>{children}</>;
}

/** Shows a permission-denied state when the account lacks an experience. Experience only. */
export function RequireExperience({
  experience,
  children,
}: {
  experience: Experience;
  children: ReactNode;
}) {
  const { session } = useAuth();
  if (!session?.experiences.includes(experience)) {
    const first = session?.experiences[0];
    return (
      <div className="mx-auto max-w-xl p-6">
        <PermissionDeniedState
          title={`Your account does not include ${EXPERIENCE_META[experience].label}`}
        >
          <p>
            Access to this area depends on your role.{' '}
            {first ? (
              <Link
                className="font-medium text-navy-900 underline"
                to={EXPERIENCE_META[first].home}
              >
                Go to {EXPERIENCE_META[first].label}
              </Link>
            ) : (
              <Link className="font-medium text-navy-900 underline" to="/account">
                View your account
              </Link>
            )}
          </p>
        </PermissionDeniedState>
      </div>
    );
  }
  return <>{children}</>;
}
